/* eslint-disable @next/next/no-img-element */

"use client";

import { cn } from "@/lib/utils";
import { mergeAttributes, Node, NodeViewProps } from "@tiptap/core";
import { NodeViewWrapper, ReactNodeViewRenderer } from "@tiptap/react";
import { TrashIcon } from "lucide-react";
import React, { useCallback, useRef, useState } from "react";

const MIN_WIDTH = 200; // Minimum width in pixels
const MIN_HEIGHT = 200; // Minimum height in pixels

const ResizableImage: React.FC<NodeViewProps> = (props) => {
	const imageRef = useRef<HTMLImageElement>(null);
	const [isResizing, setIsResizing] = useState(false);

	const { src, alt, width, height, isCover } = props.node.attrs;

	const startResizing = useCallback(
		(e: React.MouseEvent<HTMLDivElement>) => {
			e.preventDefault();

			setIsResizing(true);

			const startWidth = width || imageRef.current?.width || 0;
			const startHeight = height || imageRef.current?.height || 0;
			const startX = e.clientX;
			const startY = e.clientY;

			const onMouseMove = (e: MouseEvent) => {
				const currentX = e.clientX;
				const currentY = e.clientY;

				const deltaX = currentX - startX;
				const deltaY = currentY - startY;

				const newWidth = Math.max(MIN_WIDTH, startWidth + deltaX);
				const newHeight = Math.max(MIN_HEIGHT, startHeight + deltaY);

				props.updateAttributes({
					width: newWidth,
					height: newHeight,
				});
			};

			const onMouseUp = () => {
				setIsResizing(false);
				document.removeEventListener("mousemove", onMouseMove);
				document.removeEventListener("mouseup", onMouseUp);
			};

			document.addEventListener("mousemove", onMouseMove);
			document.addEventListener("mouseup", onMouseUp);
		},
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[props.updateAttributes, width, height]
	);

	const toggleCover = () => {
		if (!isCover) {
			// Remove cover from any other image
			props.editor.state.doc.descendants((node, pos) => {
				if (node.type.name === "customImage" && node.attrs.isCover) {
					props.editor.commands.command(({ tr }) => {
						tr.setNodeAttribute(pos, "isCover", false);
						return true;
					});
				}
			});
		}

		// Set this image's cover status
		props.updateAttributes({ isCover: !isCover });
	};

	const deleteImage = () => {
		props.deleteNode();
	};

	return (
		<NodeViewWrapper className="cursor-default">
			<div className="group relative inline-block">
				<img
					ref={imageRef}
					src={src}
					alt={alt}
					width={Math.max(width || 0, MIN_WIDTH)}
					height={Math.max(height || 0, MIN_HEIGHT)}
					className={cn(
						"max-w-full cursor-move",
						isCover ? "border-2 border-blue-500" : isResizing ? "border-2 border-red-500" : ""
					)}
				/>

				{props.selected && (
					<div
						onMouseDown={startResizing}
						className="absolute bottom-0 right-0 h-4 w-4 cursor-se-resize border border-gray-400 bg-white"
					/>
				)}

				<div className="absolute right-2 top-2 flex gap-2">
					<button
						type="button"
						onClick={toggleCover}
						className="rounded border border-gray-400 bg-white px-2 py-1 text-sm"
					>
						{isCover ? "Remove Cover" : "Set as Cover"}
					</button>

					<button
						type="button"
						onClick={deleteImage}
						className="rounded border border-gray-400 bg-white px-2 py-1 text-sm text-red-500 hover:bg-red-50"
					>
						<TrashIcon className="h-4 w-4" />
					</button>
				</div>

				{isCover && (
					<div className="absolute left-2 top-2 rounded-full bg-brand-primary px-2 py-1 text-xs font-medium text-white">
						Cover
					</div>
				)}
			</div>
		</NodeViewWrapper>
	);
};

const CustomImage = Node.create({
	name: "customImage",

	addOptions() {
		return {
			HTMLAttributes: {},
		};
	},

	group: "block",

	draggable: true,

	addAttributes() {
		return {
			src: {
				default: null,
			},
			alt: {
				default: null,
			},
			title: {
				default: null,
			},
			width: {
				default: null,
			},
			height: {
				default: null,
			},
			isCover: {
				default: false,
			},
		};
	},

	parseHTML() {
		return [
			{
				tag: "img[src]",
				getAttrs: (dom) => ({
					src: dom.getAttribute("src"),
					alt: dom.getAttribute("alt"),
					title: dom.getAttribute("title"),
					width: dom.getAttribute("width"),
					height: dom.getAttribute("height"),
					isCover: dom.dataset.isCover === "true",
				}),
			},
		];
	},

	renderHTML({ HTMLAttributes }) {
		return ["img", mergeAttributes(this.options.HTMLAttributes, HTMLAttributes)];
	},

	addNodeView() {
		return ReactNodeViewRenderer(ResizableImage);
	},
});

export default CustomImage;
